import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteUrl = 'https://vabait.co';
  private readonly defaultDescription = 'Fincas, casas, lotes, apartamentos, locales comerciales para venta y arriendo en oriente antioqueño - Rionegro, La Ceja, El Carmén de Viboral, El retiro, ...';
  private propertyMetadata: { property: any; url: string } = null;

  constructor(
    private title: Title,
    private meta: Meta
  ) {}

  update(url: string): void {
    const cleanUrl = this.cleanUrl(url);
    if (this.propertyMetadata && this.propertyMetadata.url === cleanUrl) {
      this.applyPropertyMetadata(this.propertyMetadata.property, cleanUrl);
      return;
    }
    this.propertyMetadata = null;
    const isHome = cleanUrl === '' || cleanUrl === '/app/inicio';
    const pageTitle = isHome
      ? 'Vabait inmobiliaria'
      : this.getPageTitle(cleanUrl);
    const canonical = this.getCanonical(cleanUrl);

    this.title.setTitle(pageTitle);
    this.updateMeta('description', this.defaultDescription);
    this.updateMeta('robots', 'index, follow');
    this.updateMeta('og:title', pageTitle, 'property');
    this.updateMeta('og:description', this.defaultDescription, 'property');
    this.updateMeta('og:url', canonical, 'property');
    this.updateMeta('og:type', 'website', 'property');
    this.updateMeta('og:image', `${this.siteUrl}/assets/images/logo-valorinmobiliaria.png`, 'property');
    this.updateCanonical(canonical);
  }

  updateProperty(property: any, url: string): void {
    const cleanUrl = this.cleanUrl(url);
    this.propertyMetadata = { property, url: cleanUrl };
    this.applyPropertyMetadata(property, cleanUrl);
  }

  private applyPropertyMetadata(property: any, cleanUrl: string): void {
    const title = this.getPropertyTitle(property);
    const description = this.getPropertyDescription(property);
    const image = this.getPropertyImage(property);
    const canonical = this.getCanonical(cleanUrl);

    this.title.setTitle(title);
    this.updateMeta('description', description);
    this.updateMeta('robots', 'index, follow');
    this.updateMeta('og:title', title, 'property');
    this.updateMeta('og:description', description, 'property');
    this.updateMeta('og:url', canonical, 'property');
    this.updateMeta('og:type', 'website', 'property');
    this.updateMeta('og:image', image, 'property');
    this.updateMeta('og:image:secure_url', image, 'property');
    this.updateCanonical(canonical);
  }

  private getPageTitle(url: string): string {
    if (url === '/app/inmuebles') {
      return 'Propiedades | Grupo Vabait';
    }
    if (url === '/app/colaboradores') {
      return 'Colaboradores | Grupo Vabait';
    }
    if (url === '/app/contacto') {
      return 'Contacto | Grupo Vabait';
    }
    if (url.indexOf('/app/detalle/') === 0) {
      return 'Detalle de propiedad | Grupo Vabait';
    }
    return 'Grupo Vabait | Inmobiliaria';
  }

  private getPropertyTitle(property: any): string {
    const title = this.getText(property && property.titulo);
    if (title) {
      return title;
    }

    const offer = this.getText(property && property.oferta);
    const type = this.getText(property && property.tipo);
    const city = this.getText(property && property.ciudad);
    const code = this.getText(property && property.codigo);
    const offerAndType = [offer, type].filter(Boolean).join(' ');

    if (offerAndType && city) {
      return `${offerAndType} en ${city}`;
    }
    if (type && code) {
      return `${type} código ${code}`;
    }
    if (offerAndType || city) {
      return [offerAndType, city].filter(Boolean).join(' en ');
    }

    return 'Propiedad en Vabait';
  }

  private getPropertyDescription(property: any): string {
    const description = this.getText(property && property.descripcion);
    return description || this.defaultDescription;
  }

  private getPropertyImage(property: any): string {
    const image = this.getText(property && property.portada);
    return /^https?:\/\//i.test(image)
      ? image
      : `${this.siteUrl}/assets/images/logo-valorinmobiliaria.png`;
  }

  private getText(value: any): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' && isFinite(value)) {
      return String(value);
    }
    return '';
  }

  private cleanUrl(url: string): string {
    return (url || '').split('?')[0].replace(/\/$/, '');
  }

  private getCanonical(url: string): string {
    const canonicalPath = url === '' || url === '/app/inicio' ? '/' : url;
    return `${this.siteUrl}${canonicalPath}`;
  }

  private updateMeta(name: string, content: string, attribute: 'name' | 'property' = 'name'): void {
    if (attribute === 'property') {
      document.querySelectorAll(`meta[name="${name}"]`).forEach(tag => tag.parentNode.removeChild(tag));
    }
    const selector = `${attribute}="${name}"`;
    const tag = this.meta.updateTag(
      { [attribute]: name, content },
      selector
    );
    const target = tag || document.querySelector(`meta[${selector}]`) as HTMLMetaElement;
    if (target) {
      target.setAttribute('content', content);
    } else {
      const createdTag = document.createElement('meta');
      createdTag.setAttribute(attribute, name);
      createdTag.setAttribute('content', content);
      document.head.appendChild(createdTag);
    }
  }

  private updateCanonical(url: string): void {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
